import { forwardRef, RefObject, useCallback, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import { Edit, InsertChartOutlined, KeyboardArrowRight, PhotoOutlined } from '@mui/icons-material'
import {
  Accordion,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  CheckboxProps,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { DataGridPro, GridCellParams, GridColDef, GridColumnHeaders, GridRowParams } from '@mui/x-data-grid-pro'
import classNames from 'classnames'
import { useSnackbar } from 'notistack'

import MoveDeviceModal from '../../../../components/change-folder-modals/move-device-modal'
import ChannelBubbleGroup from '../../../../components/channel-bubble/channel-bubble-group'
import DeviceActivateModal from '../../../../components/device-activate-modal'
import DeviceDeactivateModal from '../../../../components/device-deactivate-modal'
import DeviceEditModal from '../../../../components/device-edit-modal'
import DevicePauseModal from '../../../../components/device-pause-modal'
import DevicePhotoModal from '../../../../components/device-photo-modal'
import DeviceReturnModal from '../../../../components/device-return-modal'
import DeviceUnpauseModal from '../../../../components/device-unpause-modal'
import PauseDuration from '../../../../components/pause-duration'
import StatusChip from '../../../../components/status-chip'
import { AppContext, useHost } from '../../../../providers/app-provider'
import { useAuthentication } from '../../../../providers/auth-provider'
import { getFormattedDateTime, getVanityName, isGateway } from '../../../../utils/helpers'
import useFolderTheme from '../../../../utils/hooks/useFolderTheme'
import useStickyHeaders from '../../../../utils/hooks/useStickyHeaders'
import useSharedStyles from '../../../../utils/shared.styles'
import getUseCaseMatrix, { getUseCase } from '../../../../utils/usecase-matrix'
import AccountsContext from '../../context'
import useAccountStyles from '../../index.styles'

import { getDevices, groupDevices, onSavePhoto } from './helpers'
import useStyles from './index.styles'
import MoreOptionsMenu from './more-options-menu'
import TableToolbar from './table-toolbar'
import { DeviceHealthStatus, DeviceTableGroupRow, DeviceTableRow } from './types'

const TabPanelDevices = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const accountClasses = useAccountStyles
  const theme = useTheme()
  const navigate = useNavigate()
  const containerRef = useRef(null)
  const { enqueueSnackbar } = useSnackbar()
  useStickyHeaders({ containerRef })

  const {
    folder,
    searchText,
    setPausedDeviceCount,
    deviceDisplayFirmware,
    deviceHideDeactivated,
    setFirmwareVersions,
    deviceAction,
    setDeviceAction,
    deviceView,
    deviceGroupBy,
    pauseModalStatus,
    setPauseModalStatus,
    unpauseModalStatus,
    setUnpauseModalStatus,
    returnModalStatus,
    setReturnModalStatus,
    moveDeviceModalStatus,
    setMoveDeviceModalStatus,
    deactivateModalStatus,
    setDeactivateModalStatus,
    activateModalStatus,
    setActivateModalStatus,
    selectedDeviceIds,
    setSelectedDeviceIds,
    denseViewForTableMultiselect,
    setDeviceView,
    setDevicePhotoRefreshCounter,
  } = useContext(AccountsContext)

  const { auth, hasInternalUserAccess } = useAuthentication()
  const host = useHost()
  const { tenantId, pageRefreshCounter, refreshPage } = useContext(AppContext)

  const { folderTheme } = useFolderTheme(folder?.id)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [deviceTableData, setDeviceTableData] = useState<DeviceTableRow[]>([])

  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([])
  const [editDevice, setEditDevice] = useState<DeviceTableRow | undefined>(undefined)
  const [viewDevicePhoto, setViewDevicePhoto] = useState<DeviceTableRow | undefined>(undefined)

  const showHeaderColumns = !deviceAction && selectedDeviceIds.ids.size === 0
  const hideOnSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))

  // Reset collapsed groups when deviceGroupBy changes
  useEffect(() => {
    setCollapsedGroups([])
  }, [deviceGroupBy])

  // Reset collapsed groups when deviceView changes
  useEffect(() => {
    setCollapsedGroups([])
  }, [deviceView])

  // remove groupings and set to compact view when we show dense table with additional fields for RMA and Deactivate actions
  useEffect(() => {
    if (denseViewForTableMultiselect === true) {
      setDeviceView('compact')
    } else {
      setDeviceView('default')
    }
  }, [setDeviceView, denseViewForTableMultiselect])

  const getFolderDevices = useCallback(async () => {
    if (!auth.user?.access_token) return
    if (folder?.id) {
      setIsLoading(true)
      const res = await getDevices(folder.id, host)
      if (res.ok) {
        if (res.devices.length > 0) {
          setError('')
          const deviceRows = res.devices.map((d) => {
            const lastCheckIn = d.lastCheckIn && new Date(d.lastCheckIn).valueOf() > 0 ? d.lastCheckIn : null
            const activeDeviceAlerts = d.alerts
              .filter((a) => a.status === 'Active')
              .sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())
            let alertStatus = 'none'
            if (lastCheckIn && activeDeviceAlerts.find((a) => a.tags.includes('info'))) alertStatus = 'info'
            if (lastCheckIn && activeDeviceAlerts.find((a) => a.tags.includes('warning'))) alertStatus = 'warning'
            if (lastCheckIn && activeDeviceAlerts.find((a) => a.tags.includes('urgent'))) alertStatus = 'urgent'
            if (d.deviceStatus !== 'Online' && d.deviceStatus !== 'Offline' && d.deviceStatus !== 'Audible Only' && d.deviceStatus !== 'Relay')
              alertStatus = 'inactive'
            let { gatewayId } = d
            if (!gatewayId && isGateway(d.nodeType)) gatewayId = d.id

            return {
              ...d,
              deviceLocation: d.area || d.metadata?.device_location || '',
              deviceLocationNotes: d.metadata?.device_location_note || '',
              gatewayId: gatewayId || 0,
              alertStatus,
              alerts: activeDeviceAlerts, // We only care about active alerts
              latestAlertDate: activeDeviceAlerts.length > 0 && lastCheckIn ? activeDeviceAlerts[0].updatedAt : '',
              deviceHealthStatus: (d.deviceStatus === 'Deactivated' || d.deviceStatus === 'Not Activated' || d.deviceStatus === 'Extra'
                ? 'Inactive'
                : d.hasLowSignal && d.hasLowBattery
                  ? 'Low Battery & Signal'
                  : d.hasLowBattery
                    ? 'Low Battery'
                    : d.hasLowSignal
                      ? 'Low Signal'
                      : 'No Health Issues') as DeviceHealthStatus,
              nodeTypeVanity: d.nodeType.vanityName,
              isFirstOfType: false,
              isLastOfType: false,
              group: '',
              groupColor: '',
              isGroupRow: false,
              lastCheckIn,
            }
          })
          // sort resultsWithGroups by latestAlertDate at the top, and any null dates at the bottom
          deviceRows.sort((a, b) => {
            if (a.latestAlertDate === '' && b.latestAlertDate === '') return 0
            if (a.latestAlertDate === '') return 1
            if (b.latestAlertDate === '') return -1
            return new Date(b.latestAlertDate).valueOf() - new Date(a.latestAlertDate).valueOf()
          })
          setDeviceTableData(deviceRows)
          setPausedDeviceCount(deviceRows.filter((d) => d.deviceStatus === 'Paused').length)
          // get array of unique firmwareVersions
          const uniqueFirmwareVersions = Array.from(
            new Set(deviceRows.filter((d) => d.firmwareVersion && d.firmwareVersion !== '<nil>' && d.firmwareVersion !== '0').map((d) => d.firmwareVersion))
          )
          // firmware versions may be in the format X.X.X, so sort them by first, second, and third numbers
          uniqueFirmwareVersions.sort((a, b) => {
            const aParts = a.split('.').map((n) => parseInt(n, 10))
            const bParts = b.split('.').map((n) => parseInt(n, 10))
            if (aParts[0] !== bParts[0]) return aParts[0] - bParts[0]
            if (aParts[1] !== bParts[1]) return aParts[1] - bParts[1]
            return aParts[2] - bParts[2]
          })
          setFirmwareVersions(uniqueFirmwareVersions)
        }
      } else {
        setError('Something went wrong, and list of devices did not load')
      }
      setIsLoading(false)
    }
  }, [auth.user?.access_token, folder?.id, host, setFirmwareVersions, setPausedDeviceCount])

  const fetchInitialDevices = useEffectEvent(getFolderDevices)
  useEffect(() => {
    fetchInitialDevices()
  }, [folder?.id, host, pageRefreshCounter])

  const toggleGroup = useCallback((group: string) => {
    setCollapsedGroups((prev) => {
      if (!prev.includes(group)) return [...prev, group]
      return prev.filter((g) => g !== group)
    })
  }, [])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        editable: false,
        sortable: false,
        minWidth: 90,
        maxWidth: 140,
        flex: 1,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow, string>) => {
          const { group, groupColor, isGroupRow, count } = cellParams.row as DeviceTableGroupRow
          const { deviceStatus, nodeType } = cellParams.row as DeviceTableRow
          let marginLeft: string | number = 0
          if (showHeaderColumns) marginLeft = '-32px'
          if (deviceGroupBy === 'gateway' && !isGroupRow && !isGateway(nodeType)) marginLeft = '-16px'

          if (!isGroupRow) {
            return (
              <StatusChip
                status={deviceStatus.toLowerCase()}
                label={deviceStatus}
                sx={{ ...classes.deviceStatusChip(theme), ...(deviceAction ? classes.deviceStatusChipCheckbox(theme) : {}), marginLeft }}
              />
            )
          }

          let display = ''
          if (group === 'info') display = 'Info'
          if (group === 'warning') display = 'Warning'
          if (group === 'urgent') display = 'Urgent'
          if (group === 'none') display = 'No Issues'
          if (group === 'inactive') display = 'Not In Use'
          if (deviceGroupBy === 'status') display = group
          if (deviceGroupBy === 'gateway' && group === '0') display = `No Gateway`
          if (deviceGroupBy === 'gateway' && group !== '0') display = `Gateway ${group}`
          if (deviceGroupBy === 'deviceType') display = group
          if (deviceGroupBy === 'solution') display = getUseCase(group as DeviceUseCaseType).vanity
          if (deviceGroupBy === 'location') display = group || 'No Location'
          if (deviceGroupBy === 'deviceHealth') display = group

          return (
            <div style={classes.groupAccordionContainer}>
              <Accordion
                expanded={!collapsedGroups.includes(group)}
                onChange={() => toggleGroup(group)}
                square={true}
                disableGutters={true}
                sx={classes.groupAccordion}
              >
                <AccordionSummary expandIcon={<KeyboardArrowRight htmlColor={groupColor} />} sx={classes.groupAccordionSummary}>
                  <Typography sx={{ color: groupColor }}>
                    {display} ({count})
                  </Typography>
                </AccordionSummary>
              </Accordion>
            </div>
          )
        },
      },
      {
        field: 'name',
        headerName: 'Name & Location',
        editable: false,
        sortable: false,
        flex: 2,
        minWidth: 250,
        maxWidth: 400,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow, string>) => {
          const { deviceStatus, firmwareVersion, deviceUsecase, deviceLocation, deviceLocationNotes, id, vanity, nodeType, folderId } =
            cellParams.row as DeviceTableRow
          const { isGroupRow } = cellParams.row as DeviceTableGroupRow

          if (isGroupRow) return null

          const isOfflineGateway = isGateway(nodeType) && deviceStatus === 'Offline' && deviceGroupBy !== 'gateway'
          const mainTextColor = deviceStatus === 'Deactivated' ? 'textSecondary' : 'textPrimary'
          const marginBottom = isOfflineGateway ? '48px' : 0

          const versionChip = firmwareVersion && firmwareVersion !== '<nil>' && firmwareVersion !== '0' && (
            <Box sx={classes.metaTag}>
              <Typography variant="caption" color={mainTextColor}>
                {nodeType?.name === 'nbiot_leak' ? `v${firmwareVersion.substring(firmwareVersion.length - 7)}` : `v${firmwareVersion}`}
              </Typography>
            </Box>
          )

          return (
            <Grid
              container={true}
              sx={{
                gap: 1,
                marginBottom,
              }}
            >
              <Grid sx={classes.vanityNameContainer} size={12}>
                {nodeType.name === 'leak5s' && deviceStatus === 'Not Activated' ? (
                  <Typography variant="body1" sx={classes.nameLink}>
                    {getVanityName(vanity)}
                  </Typography>
                ) : (
                  <Link
                    to={`/tenant/${tenantId}/devices/${id}`}
                    color="primary"
                    underline="hover"
                    component={RouterLink}
                    title={cellParams.value}
                    sx={classes.nameLink}
                  >
                    {getVanityName(vanity)}
                  </Link>
                )}

                {denseViewForTableMultiselect === true && versionChip}
              </Grid>
              {denseViewForTableMultiselect === false && (
                <>
                  <Grid size={12}>
                    {versionChip}

                    {deviceStatus !== 'Deactivated' && (
                      <Box sx={classes.metaTag}>
                        <Typography variant="caption" color={mainTextColor}>
                          {getUseCaseMatrix()[deviceUsecase]}
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {deviceStatus !== 'Deactivated' && (deviceLocation || deviceLocationNotes) && (
                    <Grid sx={classes.locationNotes}>
                      <Typography variant="body2" color={mainTextColor} component="span">
                        {deviceLocation}
                        {deviceLocation && deviceLocationNotes && ' • '}
                      </Typography>

                      <Typography variant="body2" component="span" sx={sharedClasses.secondaryText}>
                        {deviceLocationNotes}
                      </Typography>
                    </Grid>
                  )}

                  {deviceStatus !== 'Deactivated' && (
                    <Grid size={12}>
                      <div>
                        <PauseDuration device={cellParams.row as IDeviceAPIObject} timezone={folder?.timezone} />
                      </div>
                    </Grid>
                  )}

                  {isOfflineGateway && (
                    <Alert severity="warning" icon={false} sx={classes.offlineGatewayAlert}>
                      <Link
                        to={`/tenant/${tenantId}/accounts/${folderId}?tab=devices&groupBy=gateway`}
                        color="primary"
                        underline="hover"
                        component={RouterLink}
                      >
                        Group by gateway
                      </Link>{' '}
                      to view the list of sensors that lost connection to this gateway.
                    </Alert>
                  )}
                </>
              )}
            </Grid>
          )
        },
      },
      denseViewForTableMultiselect === false && {
        field: 'deviceReading',
        headerName: 'Device Readings',
        editable: false,
        sortable: false,
        maxWidth: 500,
        flex: 5,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow, string>) => {
          const { isGroupRow } = cellParams.row as DeviceTableGroupRow
          const { deviceStatus } = cellParams.row as DeviceTableRow

          if (isGroupRow || deviceStatus === 'Deactivated') return null

          return <ChannelBubbleGroup device={cellParams.row as DeviceTableRow} />
        },
      },
      denseViewForTableMultiselect === false && {
        field: 'lastCheckIn',
        headerName: 'Last Check-in',
        editable: false,
        sortable: false,
        minWidth: 100,
        maxWidth: 150,
        flex: 1,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow, string>) => {
          const { lastCheckIn, deviceStatus } = cellParams.row as DeviceTableRow
          const { isGroupRow } = cellParams.row as DeviceTableGroupRow

          if (isGroupRow || !lastCheckIn || deviceStatus === 'Deactivated') return null

          return (
            <div>
              <Typography variant="body1" color="textPrimary">
                {getFormattedDateTime(lastCheckIn, 'UTC', 'LLL. d, yyyy')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {getFormattedDateTime(lastCheckIn, 'UTC', "h:mmaa 'UTC'")}
              </Typography>
            </div>
          )
        },
      },
      denseViewForTableMultiselect === false && {
        field: 'options-menu',
        disableColumnMenu: true,
        sortable: false,
        resizable: false,
        maxWidth: hideOnSmallScreen ? 40 : 180,
        flex: 1,
        renderHeader: () => null,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow>) => {
          const deviceRow = cellParams.row as DeviceTableRow
          const { deviceStatus, hasPhoto, id, nodeType } = deviceRow
          const { isGroupRow } = cellParams.row as DeviceTableGroupRow

          if (isGroupRow || (deviceStatus === 'Not Activated' && nodeType.name === 'leak5s')) return null

          return (
            <Grid
              container={true}
              wrap="nowrap"
              size="grow"
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              {(deviceStatus === 'Online' || deviceStatus === 'Offline' || deviceStatus === 'Paused' || deviceStatus === 'Extra') && (
                <>
                  {hasPhoto && (
                    <IconButton onClick={() => setViewDevicePhoto(deviceRow)} sx={classes.hideOnSmallScreen}>
                      <Tooltip title="Photo">
                        <PhotoOutlined />
                      </Tooltip>
                    </IconButton>
                  )}

                  <IconButton onClick={() => navigate(`/tenant/${tenantId}/devices/${id}?&tab=charts`)}>
                    <Tooltip title="Graphs" sx={classes.hideOnSmallScreen}>
                      <InsertChartOutlined />
                    </Tooltip>
                  </IconButton>

                  {hasInternalUserAccess && (
                    <IconButton onClick={() => setEditDevice(deviceRow)} sx={classes.hideOnSmallScreen}>
                      <Tooltip title="Edit">
                        <Edit />
                      </Tooltip>
                    </IconButton>
                  )}
                </>
              )}
              {<MoreOptionsMenu device={deviceRow} setEditDevice={setEditDevice} refetchDevices={getFolderDevices} />}
            </Grid>
          )
        },
      },
      // columns for dense multiselect view when RMA or Deactivate are selected
      denseViewForTableMultiselect === true && {
        field: 'uniqueId',
        headerName: 'UID',
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow, string>) => {
          const { uniqueId } = cellParams.row as DeviceTableRow

          return (
            <Grid size={12}>
              <Typography variant="body1" component="span">
                UID:
              </Typography>
              &nbsp;
              <Typography variant="body2" component="span" color="textSecondary">
                {uniqueId}
              </Typography>
            </Grid>
          )
        },
      },
      denseViewForTableMultiselect === true && {
        field: 'deliveredDate',
        headerName: 'Delivered Date',
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow, string>) => {
          const deliveredDate = (cellParams.row as DeviceTableRow)?.deliveredDate
            ? getFormattedDateTime(String((cellParams.row as DeviceTableRow).deliveredDate), 'UTC', 'MM/dd/yy')
            : null

          return deliveredDate ? (
            <Grid size={12}>
              <Typography variant="body1" component="span">
                Delivered Date
              </Typography>
              &nbsp;
              <Typography variant="body2" component="span" color="textSecondary">
                {deliveredDate}
              </Typography>
            </Grid>
          ) : (
            <Typography variant="body1" component="span">
              Delivery Date Not Found
            </Typography>
          )
        },
      },
      denseViewForTableMultiselect === true && {
        field: 'shortOrderNumber',
        headerName: 'Short Order Number',
        editable: false,
        sortable: false,
        flex: 1,
        renderCell: (cellParams: GridCellParams<DeviceTableRow | DeviceTableGroupRow, string>) => {
          const shortOrderNumber = (cellParams.row as DeviceTableRow)?.metadata?.shortOrderNumber
            ? String((cellParams.row as DeviceTableRow)?.metadata?.shortOrderNumber)
            : null

          return shortOrderNumber ? (
            <Grid size={12}>
              <Typography variant="body1" component="span">
                Short Order #
              </Typography>
              &nbsp;
              <Typography variant="body2" component="span" color="textSecondary">
                {shortOrderNumber}
              </Typography>
            </Grid>
          ) : (
            <Typography variant="body1" component="span">
              N/A
            </Typography>
          )
        },
      },
    ],
    [
      denseViewForTableMultiselect,
      hideOnSmallScreen,
      showHeaderColumns,
      deviceGroupBy,
      classes,
      sharedClasses.secondaryText,
      collapsedGroups,
      theme,
      deviceAction,
      toggleGroup,
      tenantId,
      folder?.timezone,
      hasInternalUserAccess,
      navigate,
      getFolderDevices,
    ]
  ).filter((c) => c !== false)

  const rows: (DeviceTableRow | DeviceTableGroupRow)[] = useMemo(() => {
    let results = deviceTableData
    const searchStr = searchText.toString().toLowerCase().trim()
    // Filter devices based on search text, firmware version, and deactivated status
    results = results.filter((item) => {
      let { uniqueId, nodeTypeVanity, vanity, deviceLocation } = item
      let nodeTypeName = item.nodeType.name
      uniqueId = uniqueId.toLowerCase()
      nodeTypeName = nodeTypeName.toLowerCase()
      nodeTypeVanity = nodeTypeVanity.toLowerCase()
      vanity = vanity.toLowerCase()
      deviceLocation = deviceLocation.toLowerCase()
      const id = item.id.toString()

      // filter by search text
      let shouldReturnSearchText = true
      if (searchText) {
        if (
          uniqueId.includes(searchStr) ||
          nodeTypeName.includes(searchStr) ||
          nodeTypeVanity.includes(searchStr) ||
          vanity.includes(searchStr) ||
          (deviceLocation && deviceLocation.includes(searchStr)) ||
          id.includes(searchStr)
        )
          shouldReturnSearchText = true
        else shouldReturnSearchText = false
      }

      // filter by firmware version
      let shouldReturnFirmware = true
      if (deviceDisplayFirmware.length > 0) {
        if (deviceDisplayFirmware.includes(item.firmwareVersion)) shouldReturnFirmware = true
        else shouldReturnFirmware = false
      }

      // filter out deactivated devices
      let shouldReturnHideDeactivated = true
      if (deviceHideDeactivated) {
        if (item.deviceStatus !== 'Deactivated') shouldReturnHideDeactivated = true
        else shouldReturnHideDeactivated = false
      }

      return shouldReturnSearchText && shouldReturnFirmware && shouldReturnHideDeactivated
    })
    // Group devices
    let groupedDevices = groupDevices(results, deviceGroupBy, theme)
    if (deviceView === 'compact') {
      groupedDevices = groupedDevices.filter((r) => !r.isGroupRow)
    }
    return groupedDevices
  }, [deviceDisplayFirmware, deviceGroupBy, deviceHideDeactivated, deviceTableData, deviceView, searchText, theme])

  const isRowSelectable = useCallback(
    (row: DeviceTableRow) => {
      let shouldBeSelectable = false
      if (deviceAction && !row.isGroupRow) {
        if (deviceAction === 'move') {
          const currentlySelectedCount = selectedDeviceIds.ids.size
          const isCurrentlySelected = selectedDeviceIds.ids.has(row.id)

          // If we already have 10 selected, only allow currently selected devices to remain selectable (for unselection)
          // If we have less than 10 selected, allow any device to be selected
          shouldBeSelectable = currentlySelectedCount < 10 || isCurrentlySelected
        }
        if (deviceAction === 'pause' && (row.deviceStatus === 'Online' || row.deviceStatus === 'Offline' || row.deviceStatus === 'Paused'))
          shouldBeSelectable = true
        if (deviceAction === 'unpause' && row.deviceStatus === 'Paused') shouldBeSelectable = true
        if (
          deviceAction === 'rma' &&
          (row.deviceStatus === 'Online' ||
            row.deviceStatus === 'Offline' ||
            row.deviceStatus === 'Paused' ||
            row.deviceStatus === 'Extra' ||
            (row.deviceStatus === 'Not Activated' && row.nodeType.name !== 'leak5s'))
        )
          shouldBeSelectable = true
        if (
          deviceAction === 'deactivate' &&
          (row.deviceStatus === 'Online' ||
            row.deviceStatus === 'Offline' ||
            row.deviceStatus === 'Paused' ||
            row.deviceStatus === 'Not Activated' ||
            row.deviceStatus === 'Audible Only')
        )
          shouldBeSelectable = true
      }
      return shouldBeSelectable
    },
    [deviceAction, selectedDeviceIds.ids]
  )

  const deviceGroups = useMemo(() => rows.filter((r) => r.isGroupRow), [rows])
  const selectableDevices = useMemo(() => rows.filter((r) => isRowSelectable(r as DeviceTableRow)), [rows, isRowSelectable])
  const selectedDevices = useMemo(() => deviceTableData.filter((d) => selectedDeviceIds.ids.has(d.id)), [deviceTableData, selectedDeviceIds])
  const deactivatedDevices = useMemo(() => deviceTableData.filter((d) => d.deviceStatus === 'Deactivated'), [deviceTableData])

  useEffect(() => {
    if (deviceView === 'micro') setCollapsedGroups(deviceGroups.map((g) => g.group))
  }, [deviceView, deviceGroups])

  return (
    <>
      {error && (
        <Grid container={true} sx={[sharedClasses.centerContentHorizontal, classes.errorContainer]}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      {!error && folder && (
        <>
          <Box sx={accountClasses.dataGridContainer}>
            <DataGridPro
              ref={containerRef}
              columns={columns}
              rows={rows}
              loading={isLoading}
              slots={{
                toolbar: showHeaderColumns ? () => null : () => <TableToolbar selectableDevices={selectableDevices as DeviceTableRow[]} />,
                columnHeaders: showHeaderColumns ? GridColumnHeaders : forwardRef(() => null),
                // @ts-expect-error type mismatch
                baseCheckbox: forwardRef(
                  (props: CheckboxProps, ref: ((instance: HTMLButtonElement | null) => void) | RefObject<HTMLButtonElement | null> | null | undefined) => (
                    <Checkbox
                      {...props}
                      slotProps={{ ...props.slotProps, input: { 'aria-label': 'Select row' } }}
                      ref={ref}
                      sx={{ ...classes.gridBaseCheckbox(theme), ...(!deviceAction ? classes.hideCheckbox : {}) }}
                    />
                  )
                ),
              }}
              sx={{
                ...classes.dataGrid(theme),
                '& .dataGridRow': classes.dataGridRow,
                '& .rowBandRed': classes.rowBandRed(theme),
                '& .rowBandOrange': classes.rowBandOrange(theme),
                '& .rowBandPrimaryBlue': classes.rowBandPrimaryBlue(theme),
                '& .rowBandGreen': classes.rowBandGreen(theme),
                '& .rowBandGray': classes.rowBandGray(theme),
                '& .rowBandBlue1': classes.rowBandBlue1(theme),
                '& .rowBandBlue2': classes.rowBandBlue2(theme),
                '& .rowBandBlue3': classes.rowBandBlue3(theme),
                '& .rowBandBlue4': classes.rowBandBlue4(theme),
                '& .rowBandBlue5': classes.rowBandBlue5(theme),
                '& .gatewayDevice': classes.gatewayDevice(theme),
                '& .gatewayDeviceActionMode': classes.gatewayDeviceActionMode(theme),
                '& .rowBandFirstOfType': classes.rowBandFirstOfType(theme),
                '& .rowBandLastOfType': classes.rowBandLastOfType(theme),
                '& .rowBandOnlyOfType': classes.rowBandOnlyOfType(theme),
                '& .groupRow': classes.groupRow,
                '& .collapsedRow': classes.collapsedRow,
              }}
              getRowHeight={() => 'auto'}
              getRowClassName={(params: GridRowParams<DeviceTableRow | DeviceTableGroupRow>) => {
                const { isFirstOfType, isLastOfType, groupColor, group, nodeType, gatewayId } = params.row as DeviceTableRow
                const { isGroupRow } = params.row as DeviceTableGroupRow
                const isBlueBandRow =
                  deviceGroupBy === 'gateway' || deviceGroupBy === 'deviceType' || deviceGroupBy === 'solution' || deviceGroupBy === 'location'
                return classNames('dataGridRow', {
                  rowBandRed: groupColor === theme.palette.error.main,
                  rowBandOrange: groupColor === theme.palette.warning.main,
                  rowBandPrimaryBlue: groupColor === theme.palette.info.main,
                  rowBandGreen: groupColor === theme.palette.success.main,
                  rowBandGray: groupColor === theme.palette.text.secondary,

                  rowBandBlue1: groupColor === theme.palette.blue.variant1 && isBlueBandRow,
                  rowBandBlue2: groupColor === theme.palette.blue.variant2 && isBlueBandRow,
                  rowBandBlue3: groupColor === theme.palette.blue.variant3 && isBlueBandRow,
                  rowBandBlue4: groupColor === theme.palette.blue.variant4 && isBlueBandRow,
                  rowBandBlue5: groupColor === theme.palette.blue.variant5 && isBlueBandRow,

                  gatewayDevice: deviceGroupBy === 'gateway' && gatewayId && nodeType?.name && !isGateway(nodeType),
                  gatewayDeviceActionMode: deviceGroupBy === 'gateway' && gatewayId && nodeType?.name && !isGateway(nodeType) && deviceAction,
                  rowBandFirstOfType: isFirstOfType,
                  rowBandLastOfType: isLastOfType,
                  rowBandOnlyOfType: isFirstOfType && isLastOfType,
                  groupRow: isGroupRow,
                  collapsedRow: !isGroupRow && collapsedGroups.includes(group),
                })
              }}
              autosizeOptions={{ expand: true }}
              checkboxSelection
              isRowSelectable={(params: GridRowParams<DeviceTableRow | DeviceTableGroupRow>) =>
                params.row ? isRowSelectable(params.row as DeviceTableRow) : false
              }
              disableRowSelectionOnClick
              hideFooter
              onRowSelectionModelChange={(rowSelectionModel) => setSelectedDeviceIds(rowSelectionModel)}
              showToolbar
            />
          </Box>

          <DevicePauseModal
            open={pauseModalStatus.open}
            devices={selectedDevices.length > 0 ? selectedDevices : pauseModalStatus.devices}
            onClose={() => {
              setPauseModalStatus({ open: false, devices: [] })
            }}
            onContinue={() => {
              setDeviceAction(undefined)
              setSelectedDeviceIds({
                type: 'include',
                ids: new Set(),
              })
              getFolderDevices()
            }}
          />

          <DeviceUnpauseModal
            open={unpauseModalStatus.open}
            devices={selectedDevices.length > 0 ? selectedDevices : unpauseModalStatus.devices}
            onClose={() => {
              setUnpauseModalStatus({ open: false, devices: [] })
            }}
            onContinue={() => {
              setDeviceAction(undefined)
              setSelectedDeviceIds({
                type: 'include',
                ids: new Set(),
              })
              getFolderDevices()
            }}
          />

          <DeviceReturnModal
            open={returnModalStatus.open}
            returnDevices={selectedDevices.length > 0 ? selectedDevices : returnModalStatus.devices}
            onClose={() => {
              setReturnModalStatus({ open: false, devices: [] })
            }}
            setSelectedIds={setSelectedDeviceIds}
            refetchView={getFolderDevices}
          />

          <MoveDeviceModal
            open={moveDeviceModalStatus.open}
            onClose={() => setMoveDeviceModalStatus({ open: false, devices: [] })}
            devices={selectedDevices.length > 0 ? selectedDevices : moveDeviceModalStatus.devices}
            refresh={refreshPage}
          />

          <DeviceDeactivateModal
            open={deactivateModalStatus.open}
            deactivateDevices={selectedDevices.length > 0 ? selectedDevices : deactivateModalStatus.devices}
            onClose={() => {
              setDeactivateModalStatus({ open: false, devices: [] })
            }}
            onContinue={() => {
              setDeviceAction(undefined)
              setSelectedDeviceIds({
                type: 'include',
                ids: new Set(),
              })
              getFolderDevices()
            }}
          />

          {activateModalStatus.device && (
            <DeviceActivateModal
              open={activateModalStatus.open}
              device={activateModalStatus.device}
              deactivatedDevices={deactivatedDevices}
              onClose={() => {
                setActivateModalStatus({ open: false, device: undefined })
              }}
              onContinue={() => {
                getFolderDevices()
              }}
              folderTheme={folderTheme}
            />
          )}

          {editDevice && (
            <DeviceEditModal device={editDevice} setDevice={getFolderDevices} open={true} onClose={() => setEditDevice(undefined)} folderTheme={folderTheme} />
          )}
          {viewDevicePhoto && (
            <DevicePhotoModal
              deviceId={viewDevicePhoto.id}
              open={true}
              onClose={() => setViewDevicePhoto(undefined)}
              photoPath={null}
              onSave={() => onSavePhoto({ getFolderDevices, setDevicePhotoRefreshCounter, setViewDevicePhoto, enqueueSnackbar })}
              viewFullPhoto={true}
            />
          )}
        </>
      )}
    </>
  )
}

export default TabPanelDevices
